import React from 'react';
import {Link} from 'react-router';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import moment from 'moment';
import {renderDuration} from 'lib/timeUtils';
import BlockyDropdown from 'generic/BlockyDropdown/BlockyDropdown.react';
import LoadingIndicator from 'generic/LoadingIndicator.react';
import ViewWithSidebar from 'generic/ViewWithSidebar/ViewWithSidebar.react';
import {Table, Column} from 'sg/Tables/Table/Table.react';
import QuestionDifficultyLabel from 'generic/QuestionDifficultyLabel/QuestionDifficultyLabel.react';
import assignmentV2ResultsUtils from '../assignmentV2Results.utils';
import singleStudentResultsUtils from './singleStudentResults.utils';
import appStore from 'client/AppStore';
import {AssignmentModelV3} from 'resources/augmented/Assignment/AssignmentModel.v3';
import {SectionModelV1} from 'resources/augmented/Section/SectionModel.v1';
import {StudentModelV2} from 'resources/augmented/Student/StudentModel.v2';
import classroomV2Store from 'client/Classrooms/Classroom/ClassroomV2.store';
import {Icon, Button} from '@albert-io/atomic';
import './single-student-results.scss';

export default class SingleStudentResults extends React.Component {
  render() {
    if (
      !assignmentV2ResultsUtils.isDataReady() ||
      !singleStudentResultsUtils.getAssignmentWithStudentQuestionMetaQuery().isResourcePopulated()
    ) {
      return (
        <ViewWithSidebar
          sidebarItems={[
            {
              label: 'Filters',
              content: <LoadingIndicator className='assignment-v2-results-filters-loader' />
            }
          ]}
          mainItems={[
            {
              label: 'Details',
              content: <LoadingIndicator />
            }
          ]}
        />
      );
    }
    const assignment = singleStudentResultsUtils
      .getAssignmentWithStudentQuestionMetaQuery()
      .getResource();
    const activeSectionId = assignmentV2ResultsUtils.getActiveSection(assignment).getId();
    const activeSection = singleStudentResultsUtils.getActiveSectionById(activeSectionId);
    return (
      <ViewWithSidebar
        sidebarItems={[
          {
            label: 'Filters',
            content: (
              <div className='card'>
                <BlockyDropdown
                  resetStoreOnUnmount={false}
                  className='assignment-v2-results-filters-section-selector'
                  displayProperty='title'
                  data={assignment.getSortedSections()}
                  defaultValue={assignment.getSortedSections().first()}
                  storeName={assignmentV2ResultsUtils.getSectionSelectorStoreName()}
                />
              </div>
            )
          }
        ]}
        mainItems={[
          {
            label: 'Details',
            content: (
              <SingleStudentResultsTable
                params={this.props.params}
                assignment={assignment}
                student={assignment.getStudents().find((student) => {
                  return student.getId() === appStore.routerProps.params.studentId;
                })}
                activeSection={activeSection}
              />
            )
          }
        ]}
      />
    );
  }
}

class SingleStudentResultsTable extends React.Component {
  static propTypes = {
    assignment: PropTypes.instanceOf(AssignmentModelV3),
    student: PropTypes.instanceOf(StudentModelV2),
    activeSection: PropTypes.instanceOf(SectionModelV1),
    params: PropTypes.shape({
      classId: PropTypes.string,
      studentId: PropTypes.string,
      examId: PropTypes.string
    })
  };

  render() {
    const {assignment, student, activeSection, params} = this.props;
    const questionPositions = activeSection.getQuestionPositionMap();
    const questions = activeSection.getAllQuestions();
    const {classId} = params;
    const {examId} = params;
    const studentId = this.props.student.getId();
    const nextStudent = classroomV2Store.getNextStudentAlphabetized(studentId);

    return (
      <div className='single-student-results-table card card--no-padding'>
        <div className='single-student-results-table__details'>
          <div className='single-student-results-table__button-alignment'>
            <div className='single-student-results-table__breadcrumbs'>
              {assignment.getName()} <span className='fa fa-chevron-right' />{' '}
              {activeSection.getTitle()}
            </div>
            <div>
              {nextStudent && (
                <Button
                  as={Link}
                  size='s'
                  color='secondary'
                  icon='chevron-right'
                  to={`/classes/${classId}/exams/${examId}/student/${nextStudent.getId()}`}
                >
                  Next student <Icon icon='chevron-right' className='u-mar-l_1' />
                </Button>
              )}
            </div>
          </div>
          <h1 className='single-student-results-table__heading'>{student.getFullName()}</h1>
        </div>
        <Table data={questions} storeName='SingleStudentResultsQuestionsListStore'>
          <Column
            sortable
            heading=''
            sortBy={(question) => question.getDifficulty()}
            content={(question) => (
              <QuestionDifficultyLabel short difficulty={question.getDifficulty()} />
            )}
          />
          <Column
            sortable
            heading='Title'
            content={(question) => {
              return `${questionPositions[question.getId()]}. ${question.getTitle()}`;
            }}
            grow={3}
          />
          <Column
            sortable
            align='center'
            sortBy={(question) => question.getSectionAverageTimeElapsed()}
            heading='Time spent'
            content={(question) => {
              const timeSpent = moment.duration(
                question.getGuessPlacedByStudentInAssignmentTimeElapsed()
              );
              const formatted = renderDuration(timeSpent, {style: 'hh:mm:ss'});
              return `${formatted}s`;
            }}
            grow={1}
          />
          <Column
            sortable
            align='center'
            sortBy={(question) => question.getSectionCountOfCorrectGuesses()}
            heading='Result'
            content={(question) => {
              let result;
              if (question.getGuessPlacedByStudentInAssignment()) {
                result = question.getGuessPlacedByStudentInAssignmentCorrect()
                  ? 'correct'
                  : 'incorrect';
              } else {
                result = 'not-available';
              }
              return <GuessResultIndicator result={result} />;
            }}
          />
        </Table>
      </div>
    );
  }
}

class GuessResultIndicator extends React.PureComponent {
  static propTypes = {
    result: PropTypes.oneOf(['correct', 'incorrect', 'not-available'])
  };

  render() {
    const {result} = this.props;
    return (
      <div
        className={classnames('guess-result-indicator', 'fa', `guess-result-indicator--${result}`, {
          'fa-check': result === 'correct',
          'fa-times': result === 'incorrect',
          'fa-minus': result === 'not-available'
        })}
      />
    );
  }
}
